import { useState, useEffect } from "react";
import { Colony } from "@/lib/types";
import { useColonies as useBackendColonies, useCreateColony } from "@/lib/query/hooks";
import { featureFlags } from "@/lib/api/client";

// Enhanced colonies hook with backend integration and localStorage fallback
const MOCK_COLONIES: Colony[] = [
  {
    id: "1",
    name: "Research Colony",
    description: "AI assistants for research and analysis",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "2", 
    name: "Creative Colony",
    description: "AI brains for creative projects and writing",
    createdAt: "2024-01-20T14:30:00Z",
    updatedAt: "2024-01-20T14:30:00Z",
  }
];

export function useColonies() {
  // Backend integration
  const backendQuery = useBackendColonies();
  const createColonyMutation = useCreateColony();

  // Fallback state for localStorage
  const [localColonies, setLocalColonies] = useState<Colony[]>([]);
  const [localLoading, setLocalLoading] = useState(true);

  // Load colonies from localStorage when backend is disabled
  useEffect(() => {
    if (featureFlags.backendIntegration) return;

    const loadColonies = () => {
      try {
        const stored = localStorage.getItem('colonies');
        if (stored) {
          const parsedColonies = JSON.parse(stored);
          if (Array.isArray(parsedColonies) && parsedColonies.length > 0) {
            setLocalColonies(parsedColonies);
          } else {
            // Initialize with mock colonies for new users
            setLocalColonies(MOCK_COLONIES);
            localStorage.setItem('colonies', JSON.stringify(MOCK_COLONIES));
          }
        } else {
          // Initialize with mock colonies for new users
          setLocalColonies(MOCK_COLONIES);
          localStorage.setItem('colonies', JSON.stringify(MOCK_COLONIES));
        }
      } catch (error) {
        console.error('Error loading colonies:', error);
        setLocalColonies(MOCK_COLONIES);
      } finally {
        setLocalLoading(false);
      }
    };

    loadColonies();
  }, []);
  
  const createColony = (name: string, description?: string, template?: string) => {
    if (featureFlags.backendIntegration) {
      // Use backend mutation
      return createColonyMutation.mutateAsync({
        name,
        description: description || "",
      });
    } else {
      // Use localStorage
      const newColony: Colony = {
        id: crypto.randomUUID(),
        name,
        description: description || "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const updatedColonies = [...localColonies, newColony];
      setLocalColonies(updatedColonies);
      localStorage.setItem('colonies', JSON.stringify(updatedColonies));
      
      return Promise.resolve(newColony);
    }
  };

  const deleteColony = (colonyId: string) => {
    if (featureFlags.backendIntegration) {
      // TODO: Implement backend delete when available
      console.warn('Backend colony deletion not yet implemented');
      return;
    }

    const updatedColonies = localColonies.filter(c => c.id !== colonyId);
    setLocalColonies(updatedColonies);
    localStorage.setItem('colonies', JSON.stringify(updatedColonies));
    
    // Clear related chat data
    localStorage.removeItem(`chat-${colonyId}`);
  };

  const selectColony = (colonyId: string) => {
    // Navigation logic will be handled by the component
    console.log("Selected colony:", colonyId);
  };

  // Return backend data when available, otherwise use local data
  const colonies = featureFlags.backendIntegration ? (backendQuery.data || []) : localColonies;
  const isLoading = featureFlags.backendIntegration ? backendQuery.isLoading : localLoading;
  const hasColonies = colonies.length > 0;

  return {
    colonies,
    createColony,
    deleteColony,
    selectColony,
    isLoading,
    hasColonies,
    // Additional backend states
    error: featureFlags.backendIntegration ? backendQuery.error : null,
    isCreating: featureFlags.backendIntegration ? createColonyMutation.isPending : false,
  };
}